import React, { useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, Chip, Button, TablePagination,
  Collapse, IconButton, useTheme, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Snackbar, Alert
} from '@mui/material';
import { useQuery } from 'react-query';
import {
  Assessment as Icon, Search as SearchIcon, ExpandMore, ExpandLess,
  Edit as EditIcon, PictureAsPdf as PdfIcon, DateRange
} from '@mui/icons-material';

const fetchPedidos = async () => {
  const res = await fetch('/api/pedidos', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

const fetchMensajeros = async () => {
  const res = await fetch('/api/mensajeros', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

const ESTADO_COLORS = {
  cotizacion: 'default', pendiente: 'warning', asignado: 'info',
  recogido: 'primary', en_camino: 'info', entregado: 'success', cancelado: 'error',
};

export default function ConsolidatedPage() {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedId, setExpandedId] = useState(null);
  const [assignOpen, setAssignOpen] = useState(null);
  const [selectedMensajero, setSelectedMensajero] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: pedidosData } = useQuery('pedidos-consolidated', fetchPedidos, { refetchInterval: 30000 });
  const { data: mensajerosData } = useQuery('mensajeros-consolidated', fetchMensajeros, { refetchInterval: 30000 });
  const pedidos = pedidosData?.data || [];
  const mensajeros = (mensajerosData?.data || []).filter(m => m.tipo === 'mensajero');

  const filtered = pedidos.filter(p => {
    const fecha = new Date(p.createdAt);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    if (from && fecha < from) return false;
    if (to && fecha > to) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return `#${p.id}`.includes(s) || p.cliente?.nombre?.toLowerCase().includes(s) ||
      p.mensajero?.nombre?.toLowerCase().includes(s) ||
      p.origen_direccion?.toLowerCase().includes(s) ||
      p.destino_direccion?.toLowerCase().includes(s);
  });

  const pageData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const ingresos = filtered.filter(p => p.estado === 'entregado').reduce((s, p) => s + parseFloat(p.costo_envio || 0), 0);
  const entregados = filtered.filter(p => p.estado === 'entregado').length;
  const pendientes = filtered.filter(p => p.estado === 'pendiente').length;
  const cancelados = filtered.filter(p => p.estado === 'cancelado').length;

  const handleAssign = async (pedidoId) => {
    if (!selectedMensajero) return;
    try {
      const res = await fetch(`/api/pedidos/${pedidoId}/asignar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ mensajero_id: parseInt(selectedMensajero) })
      });
      const data = await res.json();
      if (data.success) {
        setSnack({ open: true, msg: `Pedido #${pedidoId} asignado`, severity: 'success' });
        setAssignOpen(null);
        setSelectedMensajero('');
      } else {
        setSnack({ open: true, msg: data.error || 'Error', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: 'Error de conexión', severity: 'error' });
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Consolidado de Pedidos</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Ingresos</Typography>
          <Typography variant="h5" color="success.main">${ingresos.toFixed(2)}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Entregados</Typography>
          <Typography variant="h5" color="info.main">{entregados}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Pendientes</Typography>
          <Typography variant="h5" color="warning.main">{pendientes}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Cancelados</Typography>
          <Typography variant="h5" color="error.main">{cancelados}</Typography>
        </CardContent></Card></Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <DateRange sx={{ color: theme.palette.text.secondary }} />
        <TextField type="date" size="small" label="Desde" value={dateFrom}
          onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" size="small" label="Hasta" value={dateTo}
          onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField placeholder="Buscar..." value={search} size="small" sx={{ minWidth: 250 }}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: theme.palette.text.secondary }} /> }} />
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Cliente</strong></TableCell>
              <TableCell><strong>Mensajero</strong></TableCell>
              <TableCell><strong>Origen</strong></TableCell>
              <TableCell><strong>Destino</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Costo</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell align="center"><strong>Acción</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageData.map(p => (
              <React.Fragment key={p.id}>
                <TableRow hover>
                  <TableCell><strong>#{p.id}</strong></TableCell>
                  <TableCell>{p.cliente?.nombre || '—'}</TableCell>
                  <TableCell>{p.mensajero?.nombre || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.origen_direccion || '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.destino_direccion || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={p.estado} size="small" color={ESTADO_COLORS[p.estado] || 'default'} />
                  </TableCell>
                  <TableCell>${parseFloat(p.costo_envio || 0).toFixed(2)}</TableCell>
                  <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleDateString('es') : '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                      {expandedId === p.id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                    {['pendiente'].includes(p.estado) && (
                      <IconButton size="small" color="primary" onClick={() => setAssignOpen(p)}>
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0, border: 'none' }}>
                    <Collapse in={expandedId === p.id}>
                      <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary">Cliente</Typography>
                            <Typography variant="body2">{p.cliente?.nombre || '—'}</Typography>
                            <Typography variant="caption">{p.cliente?.telefono || ''}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary">Mensajero</Typography>
                            <Typography variant="body2">{p.mensajero?.nombre || '—'}</Typography>
                            <Typography variant="caption">{p.mensajero?.telefono || ''}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary">Tipo Servicio</Typography>
                            <Typography variant="body2">{p.tipo_servicio || '—'}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary">Método Pago</Typography>
                            <Typography variant="body2">{p.metodo_pago || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Descripción</Typography>
                            <Typography variant="body2">{p.descripcion || 'Sin descripción'}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {pageData.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center"><Typography color="textSecondary">Sin resultados</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </TableContainer>

      <Dialog open={!!assignOpen} onClose={() => setAssignOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Asignar Mensajero - Pedido #{assignOpen?.id}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Seleccionar Mensajero</InputLabel>
            <Select value={selectedMensajero} label="Seleccionar Mensajero"
              onChange={e => setSelectedMensajero(e.target.value)}>
              {mensajeros.filter(m => m.estado === 'activo').map(m => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nombre} ({m.vehiculo_tipo || 'sin vehículo'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => handleAssign(assignOpen?.id)} disabled={!selectedMensajero}>
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
